import { UserContext } from '@/contexts/UserContext.tsx';
import { classNames } from '@/utils';
import { handleUpload } from '@/utils/file-upload.ts';
import {
  ChevronRightIcon,
  Cog6ToothIcon,
  PhotoIcon,
} from '@heroicons/react/24/outline';
import {
  CheckCircleIcon,
  PlayPauseIcon,
  TrashIcon,
} from '@heroicons/react/24/solid';
import * as Sentry from '@sentry/react';
import { AssetData } from '@superfunnel/types';
import Button from '@superfunnel/ui-core/v2/Button';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@superfunnel/ui-core/v2/Collapsible';
import FieldTitle from '@superfunnel/ui-core/v2/FieldTitle';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@superfunnel/ui-core/v2/Select';
import {
  ToggleGroup,
  ToggleGroupItem,
} from '@superfunnel/ui-core/v2/ToggleGroup';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@superfunnel/ui-core/v2/Tooltip';
import { Plus, Trash } from 'iconoir-react';
import {
  ChangeEvent,
  DragEvent,
  useCallback,
  useContext,
  useRef,
  useState,
} from 'react';
import { uid } from 'uid';
import ImageLibraryModal from '../../../modals/ImageLibraryModal/ImageLibraryModal';

export const mergeImages = (
  newImage?: AssetData,
  oldImage?: AssetData,
): AssetData | undefined => {
  if (!newImage) return newImage;
  if (!oldImage) return newImage;

  return {
    ...oldImage,
    ...newImage,
    objectFit: newImage.objectFit ?? oldImage.objectFit ?? (null as any),
    objectPosition:
      newImage.objectPosition ?? oldImage.objectPosition ?? (null as any),
    devices: newImage.devices ?? oldImage.devices ?? (null as any),
    ...(newImage.type === 'video' && oldImage.type === 'video'
      ? {
          thumbnailImage: newImage.thumbnailImage ?? oldImage.thumbnailImage,
          openIn: newImage.openIn ?? oldImage.openIn,
        }
      : {}),
  };
};

interface PropsMulti {
  size?: 'small' | 'large';
  image?: AssetData[];
  multi: true;
  onSelected: (images?: AssetData[]) => void;
  className?: string;
  types: ('image' | 'video')[];
}

interface PropsSingle {
  size?: 'small' | 'large';
  image?: AssetData;
  multi?: false;
  onSelected: (images?: AssetData) => void;
  className?: string;
  showImageSettings?:
    | boolean
    | {
        objectFit?: boolean;
        objectPosition?: boolean;
        replaceOnMobile?: boolean;
      };
  types?: ('image' | 'video')[];
}

type Props = PropsMulti | PropsSingle;

export default function FileUploadInput({
  types = ['image'],
  size = 'large',
  ...props
}: Props) {
  const { image, multi, onSelected, className } = props;
  const id = uid();

  const { selectedAccount } = useContext(UserContext);
  const [uploadProgress, setUploadProgress] = useState<number | null>(null);
  const [dragActive, setDragActive] = useState(false);
  const inputRef = useRef<any>(null);
  const [selectImageModalOpen, setSelectImageModalOpen] = useState(false);
  const [showSettings, setShowSettings] = useState(false);

  // handle drag events
  const handleDrag = function (e: DragEvent<HTMLDivElement>) {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleFileUpload = useCallback(
    (files: File[]) => {
      if (!selectedAccount) return;

      if (files.length) {
        setUploadProgress(0);
        handleUpload(selectedAccount.id, files, (progress) =>
          setUploadProgress(Math.ceil(progress)),
        )
          .then((res: AssetData[]) => {
            setUploadProgress(null);
            if (multi) {
              onSelected(res);
            } else {
              onSelected(mergeImages(res[0], image));
            }
          })
          .catch((e) => {
            console.error(e);
            Sentry.captureException(e);
            setUploadProgress(null);
          });
      }
    },
    [selectedAccount, onSelected, multi, image],
  );

  const handleFileSelection = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const files = e.target.files?.length ? Array.from(e.target.files) : [];
      handleFileUpload(files);

      e.target.value = '';
    },
    [handleFileUpload],
  );

  const handleFileDrop = useCallback(
    (e: DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      e.stopPropagation();

      setDragActive(false);

      const files = e.dataTransfer.files?.length
        ? Array.from(e.dataTransfer.files)
        : [];
      handleFileUpload(files);

      e.dataTransfer.clearData();
    },
    [handleFileUpload],
  );

  return (
    <>
      <ImageLibraryModal
        open={selectImageModalOpen}
        onClose={() => setSelectImageModalOpen(false)}
        onSelected={(newImage) => {
          setSelectImageModalOpen(false);
          if (multi) {
            onSelected(newImage);
          } else {
            const _image = newImage?.[0];
            onSelected(mergeImages(_image, image));
          }
        }}
        types={types}
      />
      <div className="">
        <div
          onDragEnter={handleDrag}
          onDragOver={handleDrag}
          onDragLeave={handleDrag}
          onDrop={handleFileDrop}
          className={classNames(
            'w-full overflow-hidden',
            'relative flex min-h-[50%] justify-center border',
            dragActive ? 'bg-blue-100' : '',
            image
              ? 'border-base-200'
              : 'border-brand-200 bg-brand-25 border-dashed',
            size === 'small' ? 'rounded-2lg size-18' : 'h-40 w-full rounded-lg',
            className,
          )}
        >
          {(multi ? image?.length : image) ? (
            !multi ? (
              <div className="group/logo relative w-full overflow-hidden">
                {image && image?.type !== 'video' && (
                  <img
                    alt={''}
                    src={image?.url}
                    className="h-full w-full rounded-lg object-contain"
                    onClick={() => setSelectImageModalOpen(true)}
                  />
                )}
                {image && image?.type === 'video' && (
                  <div className="relative h-full w-full">
                    <video
                      id={`video-${id}`}
                      src={image?.url}
                      controls={false}
                      className="pointer-events-none -z-10 h-full w-full object-contain"
                    ></video>
                    <Button
                      variant="default"
                      size="icon-lg"
                      className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-green-600 hover:bg-green-700"
                      onClick={() => {
                        const video = document.querySelector(
                          `#video-${id}`,
                        ) as HTMLVideoElement;
                        if (video) {
                          video.paused ? video.play() : video.pause();
                        }
                      }}
                    >
                      <PlayPauseIcon className="h-8 w-8" />
                    </Button>
                  </div>
                )}
                {size === 'small' ? (
                  <div
                    className={
                      'bg-base-900/40 pointer-events-none invisible absolute top-0 left-0 flex h-full w-full items-center justify-center group-hover/logo:visible'
                    }
                  >
                    <button
                      className="pointer-events-auto text-white"
                      onClick={() => onSelected(undefined)}
                    >
                      <Trash className={'size-5'} strokeWidth={1.5} />
                    </button>
                  </div>
                ) : (
                  <div className="pointer-events-none invisible absolute bottom-0 flex w-full items-stretch gap-2 rounded-b-lg bg-white/30 p-2 backdrop-blur-lg group-hover/logo:visible">
                    <Button
                      className="pointer-events-auto"
                      onClick={() => setSelectImageModalOpen(true)}
                    >
                      Replace
                    </Button>
                    {!props.multi && props.showImageSettings && (
                      <Button
                        className="pointer-events-auto"
                        color="positive"
                        size="sm"
                        onClick={() => setShowSettings(!showSettings)}
                      >
                        <Cog6ToothIcon className="h-5 w-5" />
                      </Button>
                    )}
                    <div className="flex-1"></div>
                    <Button
                      className="pointer-events-auto"
                      color="negative"
                      size="sm"
                      onClick={() => onSelected(undefined)}
                    >
                      <TrashIcon className="h-4 w-4" />
                    </Button>
                  </div>
                )}
              </div>
            ) : (
              <>{/* not supported */}</>
            )
          ) : (
            <div
              className={classNames(
                'flex flex-col justify-center text-center',
                uploadProgress !== null ? 'pointer-events-none opacity-0' : '',
                size === 'small' ? 'w-full' : 'py-10',
              )}
            >
              {size === 'small' ? (
                <label
                  // htmlFor={'file-upload' + id}
                  className="text-brand-500 focus-within:ring-brand-500 hover:text-brand-500 flex h-full w-full cursor-pointer items-center justify-center focus-within:ring-2 focus-within:ring-offset-2 focus-within:outline-hidden"
                  onClick={() => setSelectImageModalOpen(true)}
                >
                  <Plus />
                </label>
              ) : (
                <>
                  <PhotoIcon
                    className="mx-auto h-12 w-12 text-gray-300"
                    aria-hidden="true"
                  />
                  <div className="mt-4 flex text-sm leading-6 whitespace-nowrap text-gray-600">
                    <label
                      // htmlFor={'file-upload' + id}
                      className="relative cursor-pointer rounded-md font-semibold text-indigo-600 focus-within:ring-2 focus-within:ring-indigo-600 focus-within:ring-offset-2 focus-within:outline-hidden hover:text-indigo-500"
                      onClick={() => setSelectImageModalOpen(true)}
                    >
                      <span>Select {multi ? 'files' : 'a file'}</span>
                    </label>
                    <p className="pl-1">or drag and drop</p>
                  </div>
                  <p className="text-xs leading-5 text-gray-600">
                    PNG, JPG, GIF up to 10MB
                  </p>
                </>
              )}
            </div>
          )}
          <input
            id={'file-upload' + id}
            name={'file-upload' + id}
            ref={inputRef}
            type="file"
            multiple={multi}
            accept="image/*"
            className="sr-only"
            onChange={handleFileSelection}
          />
          {uploadProgress !== null ? (
            <div className="absolute w-full">
              <UploadProgressBar progress={uploadProgress} />
            </div>
          ) : (
            <></>
          )}
        </div>
        {showSettings && !props.multi && props.showImageSettings && (
          <AssetSettings
            image={props.image}
            showImageSettings={props.showImageSettings}
            onSelected={props.onSelected}
          />
        )}
      </div>
    </>
  );
}

const UploadProgressBar = ({ progress }: { progress: number }) => {
  return (
    <div className="w-full overflow-hidden rounded-t-lg bg-gray-200">
      <div
        className="min-w-5 rounded-ss-lg bg-blue-600 p-0.5 text-center text-xs leading-none font-medium text-blue-100 transition-all"
        style={{ width: progress + '%' }}
      >
        {progress}%
      </div>
    </div>
  );
};

const AssetSettings = (props: {
  image?: AssetData;
  showImageSettings: PropsSingle['showImageSettings'];
  onSelected: PropsSingle['onSelected'];
}) => {
  if (!props.showImageSettings) return null;

  const { image } = props;

  return (
    <div className="flex flex-col gap-2 p-2">
      {image?.type !== 'video' && (
        <>
          {(props.showImageSettings === true ||
            props.showImageSettings.objectFit) && (
            <div>
              <FieldTitle className="mb-2" title={'Image Settings'} />
              <Tooltip open={props.image ? false : undefined}>
                <TooltipTrigger asChild>
                  <ToggleGroup
                    type="single"
                    variant="outline"
                    size="sm"
                    disabled={!props.image}
                    value={props.image?.objectFit ?? 'default'}
                    onValueChange={(selected: string) => {
                      if (!selected) return;
                      if (!props.image) return;

                      if (!selected || selected === 'default') {
                        delete props.image.objectFit;
                      } else {
                        props.image.objectFit =
                          selected as AssetData['objectFit'];
                      }

                      props.onSelected(props.image);
                    }}
                  >
                    <ToggleGroupItem value="default">Default</ToggleGroupItem>
                    <ToggleGroupItem value="cover">Cover</ToggleGroupItem>
                    <ToggleGroupItem value="contain">Contain</ToggleGroupItem>
                    <ToggleGroupItem value="fill">Fill</ToggleGroupItem>
                  </ToggleGroup>
                </TooltipTrigger>
                <TooltipContent>
                  <p>Add an image</p>
                </TooltipContent>
              </Tooltip>
            </div>
          )}
          {(props.showImageSettings === true ||
            props.showImageSettings.objectPosition) && (
            <div>
              <Tooltip open={props.image ? false : undefined}>
                <TooltipTrigger asChild>
                  <Select
                    disabled={!props.image}
                    value={props.image?.objectPosition ?? 'default'}
                    onValueChange={(selected: string) => {
                      if (!props.image) return;
                      if (!selected || selected === 'default') {
                        delete props.image.objectPosition;
                      } else {
                        props.image.objectPosition =
                          selected as AssetData['objectPosition'];
                      }
                      props.onSelected(props.image);
                    }}
                  >
                    <SelectTrigger className="w-fit">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="default">
                        <>
                          <span className="me-2 text-xs">Position:</span>
                          default
                        </>
                      </SelectItem>
                      <SelectItem value="left">
                        <>
                          <span className="me-2 text-xs">Position:</span>
                          left
                        </>
                      </SelectItem>
                      <SelectItem value="center">
                        <>
                          <span className="me-2 text-xs">Position:</span>
                          center
                        </>
                      </SelectItem>
                      <SelectItem value="right">
                        <>
                          <span className="me-2 text-xs">Position:</span>
                          right
                        </>
                      </SelectItem>
                      <SelectItem value="top">
                        <>
                          <span className="me-2 text-xs">Position:</span>
                          top
                        </>
                      </SelectItem>
                      <SelectItem value="bottom">
                        <>
                          <span className="me-2 text-xs">Position:</span>
                          bottom
                        </>
                      </SelectItem>
                      <SelectItem value="left top">
                        <>
                          <span className="me-2 text-xs">Position:</span>
                          left top
                        </>
                      </SelectItem>
                      <SelectItem value="left bottom">
                        <>
                          <span className="me-2 text-xs">Position:</span>
                          left bottom
                        </>
                      </SelectItem>
                      <SelectItem value="right top">
                        <>
                          <span className="me-2 text-xs">Position:</span>
                          right top
                        </>
                      </SelectItem>
                      <SelectItem value="right bottom">
                        <>
                          <span className="me-2 text-xs">Position:</span>
                          right bottom
                        </>
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </TooltipTrigger>
                <TooltipContent side="top">
                  <p>Select an image first</p>
                </TooltipContent>
              </Tooltip>
            </div>
          )}
          {(props.showImageSettings === true ||
            props.showImageSettings.replaceOnMobile) && (
            <Collapsible className="group mt-1">
              <CollapsibleTrigger className="mb-2 flex w-full justify-between text-sm leading-6 font-medium text-gray-900">
                <span>Replace On Mobile</span>
                <div className="flex items-center gap-2">
                  {!!image?.devices?.mobile && (
                    <CheckCircleIcon className="h-5 w-5 text-green-500" />
                  )}
                  <ChevronRightIcon className="h-5 w-5 transition-transform group-data-[state=open]:rotate-90" />
                </div>
              </CollapsibleTrigger>
              <CollapsibleContent>
                <FileUploadInput
                  onSelected={(newImage) => {
                    if (!image) return;

                    if (newImage) {
                      image.devices = { mobile: newImage };
                    } else {
                      delete image.devices;
                    }
                    props.onSelected(image);
                  }}
                  image={image?.devices?.mobile}
                  types={['image']}
                  showImageSettings={
                    props.showImageSettings === true
                      ? {
                          objectFit: true,
                          objectPosition: true,
                          replaceOnMobile: false,
                        }
                      : {
                          ...props.showImageSettings,
                          replaceOnMobile: false,
                        }
                  }
                />
              </CollapsibleContent>
            </Collapsible>
          )}
        </>
      )}
      {image && image?.type === 'video' ? (
        <>
          <Collapsible className="group mt-1">
            <CollapsibleTrigger className="mb-2 flex w-full justify-between text-sm leading-6 font-medium text-gray-900">
              <span>Video Thumbnail</span>
              <ChevronRightIcon className="h-5 w-5 transition-transform group-data-[state=open]:rotate-90" />
            </CollapsibleTrigger>
            <CollapsibleContent>
              <FileUploadInput
                onSelected={(newImage) => {
                  if (!image) return;
                  if (image.type !== 'video') return;

                  if (newImage && newImage.type === 'image') {
                    image.thumbnailImage = newImage;
                  } else {
                    delete image.thumbnailImage;
                  }
                  props.onSelected(image);
                }}
                image={image?.thumbnailImage}
                types={['image']}
                showImageSettings
              />
            </CollapsibleContent>
          </Collapsible>
          <Select
            value={image?.openIn ?? 'none'}
            onValueChange={(selected: string) => {
              if (!image) return;
              if (selected === 'none') {
                delete image.openIn;
              }
              if (selected === 'modal') {
                image.openIn = 'modal';
              }
              if (selected === 'hover') {
                image.openIn = 'hover';
              }
              if (selected === 'autoplay') {
                image.openIn = 'autoplay';
              }
              props.onSelected(image);
            }}
          >
            <SelectTrigger className="w-full">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="none">Open on view</SelectItem>
              <SelectItem value="modal">Open in modal</SelectItem>
              <SelectItem value="hover">Open on hover</SelectItem>
              <SelectItem value="autoplay">Autoplay</SelectItem>
            </SelectContent>
          </Select>
        </>
      ) : (
        <></>
      )}
    </div>
  );
};
